import React from "react";
import {
  CopyTextContainer,
  Dialog,
  KeyValueList,
  LoadingDots,
  LongTextLabel,
  Toast,
} from "@sscs/ui";
import {
  LinkIcon as MUILinkIcon,
  LockIcon as MUILockOutlinedIcon,
} from "@sscs/ui/icons";
import { SharedReportInfo } from "../../../../../types";
import {
  getSharedReportLink,
  isSharedReportLinkExpired,
} from "../../../../../utils";
import getReportSettingsList from "../../ReportSettings/reportSettingsList";
import styles from "./shareReportStep.module.scss";

type ShareReportStepProps = {
  sharedReport: SharedReportInfo | null;
  resentToEmailAddressesCount?: number;
  sentToEmailAddressesCount?: number;
};

const ShareReportStep = ({
  sharedReport,
  resentToEmailAddressesCount = 0,
  sentToEmailAddressesCount = 0,
}: ShareReportStepProps): React.JSX.Element | null => {
  if (!sharedReport || isSharedReportLinkExpired(sharedReport)) return null;

  const sharedReportSettings = sharedReport.settings;
  const password = sharedReport.password;
  const token = sharedReport?.token;

  const reportLink = token ? getSharedReportLink(token) : "";

  const onCopyPasswordToClipboard = () => {
    Toast.show({
      title: "Report password copied to clipboard",
      type: "info",
    });
  };

  const onCopyReportLinkToClipboard = () => {
    Toast.show({
      title: "Report link copied to clipboard",
      type: "info",
    });
  };

  if (sharedReportSettings === undefined) {
    return <LoadingDots />;
  }

  return (
    <>
      <Dialog.Section>
        <Dialog.Section.Content>
          <div className={styles["copy-field"]}>
            <p className={styles["copy-field-label"]}>
              <strong>Report link:</strong>
            </p>
            <CopyTextContainer
              id="copy-report-link-container"
              name="copy-report-link-container"
              text={reportLink}
              tooltipTitle="Click to copy the report link to your clipboard"
              onCopyToClipboard={onCopyReportLinkToClipboard}
              multiline={false}
              endAdornment={<MUILinkIcon />}
            />
          </div>
          {sharedReportSettings.password_protected &&
            password !== undefined && (
              <div className={styles["copy-field"]}>
                <p className={styles["copy-field-label"]}>
                  <strong>Auto-generated password:</strong>
                </p>
                <CopyTextContainer
                  id="copy-report-password-container"
                  name="copy-report-password-container"
                  text={password}
                  tooltipTitle="Click to copy the report password to your clipboard"
                  onCopyToClipboard={onCopyPasswordToClipboard}
                  multiline={false}
                  endAdornment={<MUILockOutlinedIcon />}
                />
              </div>
            )}
        </Dialog.Section.Content>
      </Dialog.Section>
      <Dialog.Section>
        <Dialog.Section.Title>
          <span data-testid="report-link-name">
            <LongTextLabel
              label={sharedReportSettings.label}
              maxCharacters={60}
              inline
            />
          </span>
        </Dialog.Section.Title>
        <Dialog.Section.Content>
          <KeyValueList
            list={getReportSettingsList({
              expirationDate: sharedReportSettings.expiration_date,
              sharedReportOptions: sharedReportSettings,
              resentToEmailAddressesCount,
              sentToEmailAddressesCount,
            })}
          />
        </Dialog.Section.Content>
      </Dialog.Section>
    </>
  );
};

export default ShareReportStep;
